import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppContext } from 'views/App/context';
import { DTBPackage } from 'views/App/Portal/DTB/_types/dtb';
import type { UpdateDTBPackageRequest } from './_data_source/useManageDTBPackage';
import { useQueryClient } from 'react-query';
import useDTBPackage from './_data_source/useDTBPackage';
import useManageDTBPackages from './_data_source/useManageDTBPackage';

interface DTBPackageContextValue {
  dtbPackageData: DTBPackage | null;
  editedData: DTBPackage | null;
  setEditedData: React.Dispatch<React.SetStateAction<any>>;
  isLoading: boolean;
  isError: boolean;
  error: any;
  isSaving: boolean;
  handleSave: (newData: UpdateDTBPackageRequest | null) => Promise<void>;
  isAddingDtbs: boolean;
  setIsAddingDtbs: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DTBPackageContext = createContext<DTBPackageContextValue>({} as DTBPackageContextValue);

export const DTBPackageProvider: React.FC<{ children: ReactNode; }> = ({ children }) => {
  const { id } = useParams();
  const { currentOrganizationId: orgId } = useContext(AppContext);
  const [editedData, setEditedData] = useState<DTBPackage | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isAddingDtbs, setIsAddingDtbs] = useState(false);
  const queryClient = useQueryClient();

  const dtbPackageQuery = useDTBPackage(orgId || '', id || '');
  const { update } = useManageDTBPackages();
  const navigate = useNavigate();

  useEffect(() => {
    if (dtbPackageQuery.data) {
      setEditedData(dtbPackageQuery.data);
    }
  }, [dtbPackageQuery.data]);

  const handleSave = async (newData: UpdateDTBPackageRequest | null) => {
    if (!orgId || !id || !newData) return;
    setIsSaving(true);
    try {
      // TODO: Remove this once we have a proper way to handle the jurisdiction and vertical
      const cleanedData: UpdateDTBPackageRequest = { ...newData };
      if (!cleanedData.jurisdiction?.id) {
        delete (cleanedData as any).jurisdiction;
      }
      if (!cleanedData.vertical?.id) {
        delete (cleanedData as any).vertical;
      }
      // Allow manual to be null explicitly if desired; otherwise ensure correct shape
      if (cleanedData.manual && !cleanedData.manual.id) {
        (cleanedData as any).manual = null;
      }
      await update(orgId, id, cleanedData);
      queryClient.invalidateQueries(['dtbPackage', orgId, id]);
      queryClient.invalidateQueries(['dtbPackages']);
      navigate('..');
    } catch (err) {
      console.error('Error updating DTB Package:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const value: DTBPackageContextValue = {
    dtbPackageData: dtbPackageQuery.data ?? null,
    editedData,
    setEditedData,
    isLoading: dtbPackageQuery.isFetching,
    isError: dtbPackageQuery.isError,
    error: dtbPackageQuery.isError ? dtbPackageQuery : null,
    isSaving,
    handleSave,
    isAddingDtbs,
    setIsAddingDtbs,
  };

  return <DTBPackageContext.Provider value={value}>{children}</DTBPackageContext.Provider>;
};

export const useDTBPackageContext = () => useContext(DTBPackageContext);
